/**
 * CELMAXX POS-ERP — Patrón Outbox
 *
 * Garantías:
 * - Cero pérdida: cada operación se persiste en IndexedDB antes de intentar el servidor
 * - Cero duplicados: idempotency_key en cada payload
 * - Resiliencia: backoff exponencial en reintentos
 * - Orden: las operaciones se envían en el orden de creación
 * - Observabilidad: cada estado queda registrado con timestamp
 *
 * Flujo:
 * 1. enqueueOperation() → guarda en IndexedDB con status='pending'
 * 2. startSyncWorker() → loop cada 30s procesando 'pending' y 'failed'
 * 3. processEntry() → intenta la Edge Function, actualiza status
 * 4. cleanup() → elimina 'done' con más de 7 días
 */

import { db, type OutboxEntry } from '@lib/dexie/schema'
import { supabase } from '@lib/supabase/client'
// crypto.randomUUID() es Web API nativa — sin imports

// ── CONSTANTES ───────────────────────────────────────────────

const OUTBOX_SYNC_INTERVAL_MS = 30_000 // 30 segundos
const OUTBOX_MAX_ATTEMPTS = 10
const OUTBOX_DONE_TTL_DAYS = 7

// Backoff exponencial: 30s → 1m → 5m → 15m → 1h (máximo)
const BACKOFF_SCHEDULE_MS = [
  30_000, // Intento 1
  60_000, // Intento 2
  300_000, // Intento 3 (5m)
  900_000, // Intento 4 (15m)
  3_600_000, // Intento 5+ (1h)
]

function getBackoffMs(attempts: number): number {
  const idx = Math.min(attempts, BACKOFF_SCHEDULE_MS.length - 1)
  return BACKOFF_SCHEDULE_MS[idx]
}

// ── TIPOS ────────────────────────────────────────────────────

type EnqueueOptions = Pick<
  OutboxEntry,
  | 'endpoint'
  | 'method'
  | 'payload'
  | 'idempotency_key'
  | 'entity_type'
  | 'entity_local_id'
  | 'store_id'
  | 'user_id'
>

type SyncResult =
  | { ok: true; data: unknown }
  | { ok: false; code: number; message: string; retryable: boolean }

// ── ENQUEUE ──────────────────────────────────────────────────

/**
 * Agrega una operación a la cola del outbox.
 * Se llama ANTES de intentar el servidor.
 * Si el servidor responde inmediatamente, se puede marcar como 'done' sin guardar.
 *
 * @returns El ID del entry creado en el outbox
 */
export async function enqueueOperation(options: EnqueueOptions): Promise<string> {
  const id = crypto.randomUUID()

  const entry: OutboxEntry = {
    id,
    idempotency_key: options.idempotency_key,
    endpoint: options.endpoint,
    method: options.method,
    payload: options.payload,
    status: 'pending',
    attempts: 0,
    max_attempts: OUTBOX_MAX_ATTEMPTS,
    last_error: null,
    last_attempt_at: null,
    created_at: Date.now(),
    done_at: null,
    entity_type: options.entity_type,
    entity_local_id: options.entity_local_id,
    store_id: options.store_id,
    user_id: options.user_id,
  }

  await db.outbox.add(entry)

  return id
}

/**
 * Marca un entry del outbox como 'done' con el resultado del servidor.
 * Llamar cuando el servidor ya respondió 200 y no necesita reintento.
 */
export async function markOutboxDone(id: string, _serverResponse: unknown): Promise<void> {
  await db.outbox.update(id, {
    status: 'done',
    done_at: Date.now(),
    last_error: null,
  })
}

// ── PROCESO DE UN ENTRY ──────────────────────────────────────

async function processEntry(entry: OutboxEntry): Promise<void> {
  // Marcar como 'syncing'
  await db.outbox.update(entry.id, {
    status: 'syncing',
    last_attempt_at: Date.now(),
  })

  const result = await callEdgeFunction(entry)

  if (result.ok) {
    // ✅ Éxito: marcar como done
    await db.outbox.update(entry.id, {
      status: 'done',
      done_at: Date.now(),
      last_error: null,
    })
    return
  }

  // ❌ Error
  const newAttempts = entry.attempts + 1
  console.warn(
    `[Outbox] ❌ Fallo intento ${newAttempts}: ${entry.entity_type}`,
    `Code: ${result.code}`,
    `Msg: ${result.message}`
  )

  if (!result.retryable || newAttempts >= entry.max_attempts) {
    // No se puede reintentar: requiere intervención manual
    await db.outbox.update(entry.id, {
      status: 'failed',
      attempts: newAttempts,
      last_error: `[${result.code}] ${result.message}`,
      last_attempt_at: Date.now(),
    })

    if (newAttempts >= entry.max_attempts) {
      console.error(
        `[Outbox] 🚨 Máximo de intentos alcanzado: ${entry.entity_type} (${entry.id.slice(0, 8)})`,
        'Intervención manual requerida.'
      )
      // TODO: notificar al usuario via toast permanente
    }
    return
  }

  // Reintentar en el próximo ciclo (marcar como 'pending')
  await db.outbox.update(entry.id, {
    status: 'pending',
    attempts: newAttempts,
    last_error: `[${result.code}] ${result.message}`,
    last_attempt_at: Date.now(),
  })
}

// ── LLAMADA A EDGE FUNCTION ──────────────────────────────────

async function callEdgeFunction(entry: OutboxEntry): Promise<SyncResult> {
  try {
    const { data: session } = await supabase.auth.getSession()
    const token = session?.session?.access_token

    if (!token) {
      return {
        ok: false,
        code: 401,
        message: 'Sin sesión activa. Re-login requerido.',
        retryable: false,
      }
    }

    const response = await fetch(entry.endpoint, {
      method: entry.method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Idempotency-Key': entry.idempotency_key,
      },
      body: JSON.stringify(entry.payload),
      signal: AbortSignal.timeout(30_000), // 30s timeout
    })

    // 200: éxito real
    if (response.ok) {
      const data = await response.json()
      return { ok: true, data }
    }

    // 409: el servidor ya tiene este registro (idempotente = ok)
    if (response.status === 409) {
      return { ok: true, data: null }
    }

    // 422: validación falló (no reintentar, el payload es inválido)
    if (response.status === 422) {
      let message = 'Error de validación del servidor'
      try {
        const body = await response.json()
        message = body?.error ?? body?.message ?? message
      } catch {
        /* non-JSON body: use default message */
      }
      return { ok: false, code: 422, message, retryable: false }
    }

    // 401/403: sin permiso (no reintentar)
    if (response.status === 401 || response.status === 403) {
      return {
        ok: false,
        code: response.status,
        message: 'Sin autorización. Verificar sesión.',
        retryable: false,
      }
    }

    // 5xx: error del servidor (reintentar con backoff)
    if (response.status >= 500) {
      return {
        ok: false,
        code: response.status,
        message: `Error del servidor: ${response.status}`,
        retryable: true,
      }
    }

    // Otros 4xx: reintentar no vale la pena
    return {
      ok: false,
      code: response.status,
      message: `Error HTTP ${response.status}`,
      retryable: false,
    }
  } catch (err) {
    // Error de red (sin conexión, timeout)
    const isNetworkError =
      err instanceof TypeError || (err instanceof DOMException && err.name === 'AbortError')

    return {
      ok: false,
      code: 0,
      message: isNetworkError
        ? 'Sin conexión a internet'
        : `Error inesperado: ${err instanceof Error ? err.message : String(err)}`,
      retryable: true, // Los errores de red siempre son reintentables
    }
  }
}

// ── FLUSH MANUAL ─────────────────────────────────────────────

/**
 * Procesar el outbox inmediatamente (sin esperar al ciclo automático).
 * Llamar cuando el usuario recupera conexión o al montar la app.
 */
export async function flushOutbox(): Promise<{ processed: number; errors: number }> {
  if (!navigator.onLine) {
    return { processed: 0, errors: 0 }
  }

  // Obtener entries en orden de creación (FIFO)
  // Solo los que no están en 'syncing' actualmente y tienen intentos disponibles
  const pending = await db.outbox.where('status').anyOf(['pending', 'failed']).sortBy('created_at')

  // Filtrar los que deben esperar su backoff
  const now = Date.now()
  const ready = pending.filter(entry => {
    if (entry.attempts === 0) return true
    const waitMs = getBackoffMs(entry.attempts)
    const elapsed = now - (entry.last_attempt_at ?? 0)
    return elapsed >= waitMs
  })

  if (ready.length === 0) {
    return { processed: 0, errors: 0 }
  }

  let processed = 0
  let errors = 0

  // Procesar en serie (no en paralelo, para respetar orden)
  for (const entry of ready) {
    try {
      await processEntry(entry)
      const updated = await db.outbox.get(entry.id)
      if (updated?.status === 'done') {
        processed++
      } else {
        errors++
      }
    } catch (err) {
      errors++
      console.error(`[Outbox] Error procesando ${entry.id}:`, err)
    }
  }

  return { processed, errors }
}

// ── WORKER AUTOMÁTICO ────────────────────────────────────────

let syncIntervalId: ReturnType<typeof setInterval> | null = null
let isSyncing = false

/**
 * Iniciar el worker de sync automático en background.
 * Llamar una sola vez al montar la app (en App.tsx o SyncProvider).
 */
export function startSyncWorker(): () => void {
  if (syncIntervalId) {
    console.warn('[Outbox] Worker ya está corriendo.')
    return () => stopSyncWorker()
  }

  // Intentar flush inmediato al iniciar
  flushOutbox()

  // Programar flush periódico
  syncIntervalId = setInterval(async () => {
    if (isSyncing || !navigator.onLine) return
    isSyncing = true
    try {
      await flushOutbox()
    } finally {
      isSyncing = false
    }
  }, OUTBOX_SYNC_INTERVAL_MS)

  // Flush al recuperar conexión
  const onOnline = () => {
    flushOutbox()
  }
  window.addEventListener('online', onOnline)

  // Cleanup function
  return () => {
    stopSyncWorker()
    window.removeEventListener('online', onOnline)
  }
}

export function stopSyncWorker(): void {
  if (syncIntervalId) {
    clearInterval(syncIntervalId)
    syncIntervalId = null
  }
}

// ── LIMPIEZA ─────────────────────────────────────────────────

/**
 * Eliminar entries 'done' con más de 7 días.
 * Llamar una vez al día o al hacer login.
 */
export async function cleanupOutbox(): Promise<number> {
  const cutoff = Date.now() - OUTBOX_DONE_TTL_DAYS * 24 * 60 * 60 * 1000

  const toDelete = await db.outbox
    .where('status')
    .equals('done')
    .filter(entry => (entry.done_at ?? 0) < cutoff)
    .primaryKeys()

  await db.outbox.bulkDelete(toDelete as string[])

  return toDelete.length
}

// ── ESTADÍSTICAS ─────────────────────────────────────────────

export interface OutboxStats {
  pending: number
  syncing: number
  failed: number
  done: number
  total: number
}

export async function getOutboxStats(): Promise<OutboxStats> {
  const [pending, syncing, failed, done] = await Promise.all([
    db.outbox.where('status').equals('pending').count(),
    db.outbox.where('status').equals('syncing').count(),
    db.outbox.where('status').equals('failed').count(),
    db.outbox.where('status').equals('done').count(),
  ])

  return {
    pending,
    syncing,
    failed,
    done,
    total: pending + syncing + failed + done,
  }
}

/** ¿Hay operaciones sin sincronizar? */
export async function hasPendingOperations(): Promise<boolean> {
  const count = await db.outbox.where('status').anyOf(['pending', 'syncing', 'failed']).count()
  return count > 0
}

/** Obtener los entries fallidos para mostrar en UI */
export async function getFailedEntries(): Promise<OutboxEntry[]> {
  return db.outbox.where('status').equals('failed').sortBy('created_at')
}

/** Reintentar manualmente un entry fallido */
export async function retryFailedEntry(id: string): Promise<void> {
  await db.outbox.update(id, {
    status: 'pending',
    attempts: 0,
    last_error: null,
    last_attempt_at: null,
  })
  await flushOutbox()
}
